import type { ProjectFile } from '../types';

export interface CompileError {
  title: string;
  message: string;
  suggestion?: string;
}

export interface CompileDiagnostic {
  file: string | null;
  line: number;
  column: number;
  endLine: number;
  endColumn: number;
  severity: 'error' | 'warning';
  message: string;
}

interface ParsedCompilerPayload {
  parsed: any | null;
  text: string;
}

const GENERIC_COMPILER_SERVICE_MESSAGE = 'The PDF compiler failed before it could report a LaTeX error.';
const GENERIC_COMPILER_SERVICE_SUGGESTION = 'Try compiling again. If it keeps happening, the issue is likely with the compiler service rather than your document.';

function parseCompilerPayload(raw: string): ParsedCompilerPayload {
  if (!raw.trimStart().startsWith('{')) {
    return { parsed: null, text: raw };
  }

  try {
    const parsed = JSON.parse(raw);
    const textParts: string[] = [];

    if (typeof parsed.message === 'string') textParts.push(parsed.message);
    if (typeof parsed.error === 'string') textParts.push(parsed.error);
    if (typeof parsed.logs === 'string') textParts.push(parsed.logs);

    if (parsed.log_files && typeof parsed.log_files === 'object') {
      for (const value of Object.values(parsed.log_files)) {
        if (typeof value === 'string') textParts.push(value);
      }
    }

    return {
      parsed,
      text: textParts.join('\n'),
    };
  } catch {
    return { parsed: null, text: raw };
  }
}

function normalizeDiagnosticFile(file: string, mainFileName: string): string {
  const trimmed = file.replace(/^\.\//, '');
  if (trimmed === '__main_document__.tex') return mainFileName;
  return trimmed;
}

export function parseCompileDiagnostics(
  raw: string,
  mainFileName = 'main.tex'
): CompileDiagnostic[] {
  const { text } = parseCompilerPayload(raw);
  const diagnostics = new Map<string, CompileDiagnostic>();
  const lines = text.split('\n');

  for (const line of lines) {
    const match = line.match(/^\.?\/?([^:\n]+):(\d+):\s+(.*)$/);
    if (!match) continue;

    const [, rawFile, rawLine, rawMessage] = match;
    const message = rawMessage.trim();
    if (!message || message.startsWith('==>')) continue;

    const file = normalizeDiagnosticFile(rawFile.trim(), mainFileName);
    const lineNumber = Number(rawLine);
    if (!Number.isFinite(lineNumber) || lineNumber < 1) continue;

    const severity: 'error' | 'warning' = /\bwarning\b/i.test(message) ? 'warning' : 'error';
    const key = `${file}:${lineNumber}:${severity}:${message}`;

    if (!diagnostics.has(key)) {
      diagnostics.set(key, {
        file,
        line: lineNumber,
        column: 1,
        endLine: lineNumber,
        endColumn: Number.MAX_SAFE_INTEGER,
        severity,
        message,
      });
    }
  }

  return Array.from(diagnostics.values());
}

function stripTexComments(content: string): string {
  return content.split('\n').map((line) => {
    let stripped = '';
    let backslashRunLength = 0;

    for (const char of line) {
      if (char === '%' && backslashRunLength % 2 === 0) {
        break;
      }

      stripped += char;
      backslashRunLength = char === '\\' ? backslashRunLength + 1 : 0;
    }

    return stripped;
  }).join('\n');
}

function normalizeProjectPath(path: string): string {
  return path.replace(/^\.\//, '').trim();
}

function extractImportedTextPaths(content: string): string[] {
  const uncommented = stripTexComments(content);
  const importPattern = /\\(?:lstinputlisting|VerbatimInput|verbatiminput)(?:\[[^\]]*\])?\{([^}]+)\}/g;
  const imports = new Set<string>();
  let match: RegExpExecArray | null;

  while ((match = importPattern.exec(uncommented)) !== null) {
    const importedPath = match[1]?.trim();
    if (!importedPath) continue;
    imports.add(normalizeProjectPath(importedPath));
  }

  return Array.from(imports);
}

/**
 * Check for characters that are genuinely problematic in pdflatex code
 * listings — smart quotes, emoji, non-Latin scripts, special Unicode
 * punctuation, etc.  Plain extended-Latin (accented letters) are excluded
 * because they usually work fine with inputenc / listings.
 */
function containsProblematicChars(content: string): RegExpMatchArray | null {
  // Match characters that pdflatex listings actually choke on:
  // - smart quotes U+2018–U+201F
  // - em/en dashes U+2013–U+2014
  // - ellipsis U+2026
  // - emoji and symbols above U+2100
  // - zero-width / BOM characters
  // eslint-disable-next-line no-misleading-character-class
  return content.match(/[\u2013\u2014\u2018-\u201F\u2026\uFEFF\u200B-\u200F\u2100-\uFFFF\u{1F000}-\u{1FFFF}]/u);
}

function detectImportedSourceEncodingIssue(allFiles: ProjectFile[]): CompileError | null {
  const texFiles = allFiles.filter((file) => (
    file.type === 'tex' || file.name.toLowerCase().endsWith('.tex')
  ));
  const textFiles = new Map(
    allFiles
      .filter((file) => file.type !== 'folder' && file.type !== 'image')
      .map((file) => [normalizeProjectPath(file.name), file] as const)
  );

  for (const texFile of texFiles) {
    for (const importedPath of extractImportedTextPaths(texFile.content)) {
      const importedFile = textFiles.get(importedPath);
      if (!importedFile) continue;
      const badMatch = containsProblematicChars(importedFile.content);
      if (!badMatch) continue;

      const charHex = badMatch[0].codePointAt(0)!.toString(16).toUpperCase();
      return {
        title: 'Imported Code Listing Failed',
        message: `${importedFile.name} contains a character (U+${charHex.padStart(4, '0')}) that pdflatex cannot render in a code listing.`,
        suggestion: `Look for smart quotes, em-dashes, or other special Unicode in ${importedFile.name} and replace them with ASCII equivalents.`,
      };
    }
  }

  return null;
}

/**
 * Parse a raw LaTeX compilation error string into a structured error
 * with a user-friendly title, message, and optional suggestion.
 *
 * Patterns are checked in order — first match wins.
 */
export function parseCompileError(raw: string): CompileError {
  const { parsed, text } = parseCompilerPayload(raw);
  const normalizedText = text.trim();

  const commandPackageHints: Record<string, { pkg: string; message?: string }> = {
    // Figures and links
    '\\includegraphics': { pkg: 'graphicx' },
    '\\graphicspath': { pkg: 'graphicx' },
    '\\url': { pkg: 'url', message: 'or use hyperref if you also need links' },
    '\\href': { pkg: 'hyperref' },
    '\\autoref': { pkg: 'hyperref' },

    // Quotes and text utilities
    '\\enquote': { pkg: 'csquotes' },
    '\\foreignquote': { pkg: 'csquotes' },
    '\\xspace': { pkg: 'xspace' },
    '\\setstretch': { pkg: 'setspace' },
    '\\epigraph': { pkg: 'epigraph' },
    '\\uline': { pkg: 'ulem' },
    '\\uwave': { pkg: 'ulem' },
    '\\sout': { pkg: 'ulem' },
    '\\xout': { pkg: 'ulem' },

    // Math
    '\\mathbb': { pkg: 'amsfonts' },
    '\\dfrac': { pkg: 'amsmath' },
    '\\tfrac': { pkg: 'amsmath' },
    '\\eqref': { pkg: 'amsmath' },
    '\\text': { pkg: 'amsmath' },
    '\\operatorname': { pkg: 'amsmath' },
    '\\DeclareMathOperator': { pkg: 'amsmath' },
    '\\boldsymbol': { pkg: 'amsmath' },
    '\\bm': { pkg: 'bm' },

    // Units
    '\\SI': { pkg: 'siunitx' },
    '\\si': { pkg: 'siunitx' },
    '\\num': { pkg: 'siunitx' },
    '\\qty': { pkg: 'siunitx' },

    // Tables and captions
    '\\toprule': { pkg: 'booktabs' },
    '\\midrule': { pkg: 'booktabs' },
    '\\bottomrule': { pkg: 'booktabs' },
    '\\cmidrule': { pkg: 'booktabs' },
    '\\multirow': { pkg: 'multirow' },
    '\\makecell': { pkg: 'makecell' },
    '\\subcaption': { pkg: 'subcaption' },
    '\\captionsetup': { pkg: 'caption' },
    '\\FloatBarrier': { pkg: 'placeins' },

    // Citations and bibliography
    '\\citet': { pkg: 'natbib' },
    '\\citep': { pkg: 'natbib' },
    '\\citealt': { pkg: 'natbib' },
    '\\citeauthor': { pkg: 'natbib' },
    '\\parencite': { pkg: 'biblatex' },
    '\\textcite': { pkg: 'biblatex' },
    '\\printbibliography': { pkg: 'biblatex' },
    '\\addbibresource': { pkg: 'biblatex' },

    // Code and diagrams
    '\\lstinline': { pkg: 'listings' },
    '\\lstset': { pkg: 'listings' },
    '\\mintinline': { pkg: 'minted' },
    '\\minted': { pkg: 'minted', message: 'and enable -shell-escape if needed' },
    '\\tikzset': { pkg: 'tikz' },
    '\\pgfplotsset': { pkg: 'pgfplots' },

    // Cross-references and glossary
    '\\cref': { pkg: 'cleveref' },
    '\\Cref': { pkg: 'cleveref' },
    '\\gls': { pkg: 'glossaries' },
    '\\Gls': { pkg: 'glossaries' },
    '\\acrshort': { pkg: 'glossaries' },

    // Notes
    '\\todo': { pkg: 'todonotes' },
  };
  const environmentPackageHints: Record<string, string> = {
    align: 'amsmath',
    'align*': 'amsmath',
    gather: 'amsmath',
    'gather*': 'amsmath',
    multline: 'amsmath',
    'multline*': 'amsmath',
    tikzpicture: 'tikz',
    axis: 'pgfplots',
    subfigure: 'subcaption',
  };

  // Try to detect JSON error responses (e.g. RESOURCE_FETCH_FAILURE from YtoTech)
  if (parsed?.error === 'RESOURCE_FETCH_FAILURE') {
    return {
      title: 'Resource Fetch Failed',
      message: 'The compiler could not fetch a required resource.',
      suggestion: 'Check that all referenced files are uploaded.',
    };
  }

  if (parsed?.error === 'SERVER_ERROR') {
    const detail = typeof parsed.message === 'string' && parsed.message.trim()
      ? parsed.message.trim()
      : typeof parsed.detail === 'string' && parsed.detail.trim()
        ? parsed.detail.trim()
        : null;

    return {
      title: 'Compiler Service Error',
      message: detail || GENERIC_COMPILER_SERVICE_MESSAGE,
      suggestion: GENERIC_COMPILER_SERVICE_SUGGESTION,
    };
  }

  if (parsed && !text) {
    const detail = parsed.error || parsed.message || 'Unknown error';
    return {
      title: 'Compilation Failed',
      message: detail,
    };
  }

  if (normalizedText === 'SERVER_ERROR' || normalizedText.includes('\nSERVER_ERROR')) {
    return {
      title: 'Compiler Service Error',
      message: GENERIC_COMPILER_SERVICE_MESSAGE,
      suggestion: GENERIC_COMPILER_SERVICE_SUGGESTION,
    };
  }

  if (
    /Unicode character .* not set up/i.test(text)
    || (/Unicode character/i.test(text) && /inputenc/i.test(text))
    || /Invalid UTF-8 byte/i.test(text)
    || /text line contains an invalid character/i.test(text)
  ) {
    return {
      title: 'Unsupported Character in Imported File',
      message: 'The PDF compiler hit a non-ASCII character in a file imported into the document.',
      suggestion: 'Replace smart quotes, emoji, arrows, or Unicode dashes with ASCII in the imported file, or remove the code listing from the PDF.',
    };
  }

  // Undefined control sequence — specific commands first
  if (text.includes('Undefined control sequence')) {
    const cmdMatch = text.match(/\\([a-zA-Z]+)/);
    const extractedCmd = cmdMatch ? `\\${cmdMatch[1]}` : null;

    if (extractedCmd && commandPackageHints[extractedCmd]) {
      const hint = commandPackageHints[extractedCmd];
      const extra = hint.message ? ` ${hint.message}` : '';
      return {
        title: 'Missing Package',
        message: `The ${extractedCmd} command requires the ${hint.pkg} package.`,
        suggestion: `Add \\usepackage{${hint.pkg}} to your preamble${extra}.`,
      };
    }

    for (const [cmd, hint] of Object.entries(commandPackageHints)) {
      if (text.includes(cmd)) {
        const extra = hint.message ? ` ${hint.message}` : '';
        return {
          title: 'Missing Package',
          message: `The ${cmd} command requires the ${hint.pkg} package.`,
          suggestion: `Add \\usepackage{${hint.pkg}} to your preamble${extra}.`,
        };
      }
    }

    const cmdName = extractedCmd || 'a command';
    return {
      title: 'Undefined Command',
      message: `LaTeX does not recognize ${cmdName}.`,
      suggestion: 'Check spelling or add the required \\usepackage.',
    };
  }

  // Undefined environment
  const envMatch = text.match(/Environment\s+([a-zA-Z*]+)\s+undefined/i);
  if (envMatch) {
    const envName = envMatch[1];
    const pkg = environmentPackageHints[envName];
    if (pkg) {
      return {
        title: 'Missing Package',
        message: `The ${envName} environment requires the ${pkg} package.`,
        suggestion: `Add \\usepackage{${pkg}} to your preamble.`,
      };
    }
    return {
      title: 'Undefined Environment',
      message: `LaTeX does not recognize the ${envName} environment.`,
      suggestion: 'Check spelling or add the package that defines this environment.',
    };
  }

  // File not found
  if (/File ['`].*[''] not found/i.test(text) || text.includes('File not found')) {
    const fileMatch = text.match(/File ['`](.*?)['']/);
    const fileName = fileMatch ? fileMatch[1] : 'a file';
    return {
      title: 'File Not Found',
      message: `LaTeX cannot find ${fileName}.`,
      suggestion: 'Make sure the file is uploaded and the name matches exactly.',
    };
  }

  // Missing \begin{document}
  if (text.includes('Missing \\begin{document}') || text.includes('missing \\begin{document}')) {
    return {
      title: 'Document Structure Error',
      message: 'LaTeX expected a \\begin{document} but did not find one.',
      suggestion: 'Add \\begin{document} after your preamble.',
    };
  }

  // Math mode errors
  if (text.includes('Missing $ inserted') || text.includes('Missing \\$ inserted')) {
    return {
      title: 'Math Mode Error',
      message: 'LaTeX found math symbols outside of math mode.',
      suggestion: 'Wrap math expressions in $...$ or \\[...\\].',
    };
  }

  // Bibliography errors
  if (text.includes('Undefined citation') || text.includes('Empty bibliography') || text.includes('empty bibliography')) {
    return {
      title: 'Bibliography Error',
      message: 'One or more citations could not be resolved.',
      suggestion: 'Check that your .bib file is uploaded and citation keys match.',
    };
  }

  if (text.includes("Too many }'s")) {
    return {
      title: 'Brace Mismatch',
      message: 'LaTeX found an extra closing brace.',
      suggestion: 'Remove the unmatched } highlighted in the editor.',
    };
  }

  // Fatal / emergency stop
  if (text.includes('Emergency stop') || text.includes('Fatal error') || text.includes('fatal error')) {
    return {
      title: 'Fatal Error',
      message: 'LaTeX encountered a fatal error and stopped.',
      suggestion: 'Check for unmatched braces or missing \\end commands.',
    };
  }

  // Empty / invalid PDF response (our own thrown errors)
  if (text.includes('empty PDF response')) {
    return {
      title: 'Compilation Failed',
      message: 'The compiler returned an empty PDF.',
      suggestion: 'This usually means a syntax error prevented PDF generation. Check your document.',
    };
  }

  if (text.includes('invalid PDF response')) {
    return {
      title: 'Compilation Failed',
      message: 'The compiler returned an invalid response instead of a PDF.',
      suggestion: 'Check for major syntax errors in your document.',
    };
  }

  if (parsed?.error === 'COMPILATION_ERROR') {
    const diagnostics = parseCompileDiagnostics(raw);
    if (diagnostics.length > 0) {
      const first = diagnostics[0];
      const location = first.file ? `${first.file}:${first.line}` : `line ${first.line}`;
      return {
        title: 'Compilation Error',
        message: `${location}: ${first.message}`,
        suggestion: 'The failing line is highlighted in the editor.',
      };
    }
  }

  // Generic fallback
  return {
    title: 'Compilation Failed',
    message: text.length > 200 ? text.slice(0, 200) + '...' : text,
    suggestion: 'Check your document for syntax errors.',
  };
}

export function parseCompileErrorWithContext(
  raw: string,
  allFiles: ProjectFile[]
): CompileError {
  const parsedError = parseCompileError(raw);
  const isGenericServerError = (
    parsedError.title === 'Compiler Service Error'
    && parsedError.message === GENERIC_COMPILER_SERVICE_MESSAGE
  );

  if (!isGenericServerError) {
    return parsedError;
  }

  const encodingIssue = detectImportedSourceEncodingIssue(allFiles);
  if (encodingIssue) {
    return {
      ...encodingIssue,
      suggestion: encodingIssue.suggestion
        + ' If the file looks clean, this may be a transient compiler service error — try compiling again.',
    };
  }

  return parsedError;
}
